import axios from "axios";
import {
  API_BASE_URL,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
  O_CLIENT_APP,
  O_API_KEY,
} from "constants";
import tokenRefreshService from "./tokenRefreshService";
import paths from "routes/paths";
import {
  getCurrentAppPath,
  savePostLoginRedirect,
} from "utils/postLoginRedirect";

const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILES_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";

const parseErrorPayload = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (data && typeof data === "object") {
    return {
      status: status ?? data.code,
      message: data.message || data.error || null,
    };
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object") {
        return {
          status: status ?? parsed.code,
          message: parsed.message || parsed.error || data,
        };
      }
    } catch {
      return { status, message: data };
    }
    return { status, message: data };
  }

  return { status, message: null };
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "O-API-KEY": O_API_KEY,
    "O-CLIENT-APP": O_CLIENT_APP,
    // Backend services (e.g. payment-service) expect the lowercase, hyphenated app string.
    [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
    "X-API-KEY": O_API_KEY, // for Projects Provider
  }
});

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("authToken");
  const clientId = localStorage.getItem("clientId");

  const isRegistrationRequest = 
    config.url?.includes('/clients/register/request') ||
    config.url?.includes('/clients/register/confirm');
  
  if (clientId && !isRegistrationRequest) {
    config.headers["O-CLIENT-ID"] = clientId;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const { status, message } = parseErrorPayload(error);

    if (message) {
      const normalizedMessage =
        Number(status) === 404 && FILE_IDS_NOT_FOUND_PATTERN.test(message)
          ? FILES_NOT_READY_MESSAGE
          : message;
      error.message = normalizedMessage;
    }

    if (
      error.response?.status === 401 &&
      !original._retry &&
      sessionStorage.getItem("refreshToken")
    ) {
      original._retry = true;

      try {
        await tokenRefreshService.refreshTokens();
        original.headers.Authorization = `Bearer ${sessionStorage.getItem("authToken")}`;
        return apiClient(original);
      } catch {
        savePostLoginRedirect(getCurrentAppPath());
        window.location.href = paths.simpleSessionExpired;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
